import { CheckCircleIcon } from '@heroicons/react/24/solid'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useQuery } from '@tanstack/react-query'
import { QUERY_KEYS } from 'constants/constants'
import { ActivityIndicator } from '..'
import { REQUIREMENTPROFILE } from '../../constants/evaluation-types'
import trackEvents from '../../constants/track-events'
import {
  useLanguageContext,
  useNotificationContext,
  usePartnerContext,
  useProductTourContext
} from '../../context'
import { query } from '../../graphql'
import { notifyBugsnag, uuidSmall } from '../../helpers'
import { trackEvent } from '../../helpers/analytics'
import { useCreateCareerNavigation } from '../../hooks/use-create-career-navigation'
import { useMount } from '../../hooks/use-mount'
import CreateCareerFooter from '../create-career/CreateCareerFooter'
import Button from '../tailwind/Button'
import Analyses from './career-analyse/analyses'
import ModalInviteExperts from './career-analyse/modal-invite-experts'
import ModalSelectCareer from './career-analyse/modal-select-career'
import ModalSelectPreset from './career-analyse/modal-select-preset'

const TRANSLATION_LOCATION = 'components.career.expert_analysis.'

const CareerAnalyse = ({ career, careerMutation }) => {
  const { t } = useTranslation()
  const { partner } = usePartnerContext()
  const { language } = useLanguageContext()
  const { success, error, alert } = useNotificationContext()

  const [uuid, setUuid] = useState(uuidSmall())
  const [openModal, setOpenModal] = useState(null)
  const [isLoadingSynthesizeAnalyze, setIsLoadingSynthesizeAnalyze] =
    useState(false)

  const {
    setProductTourState,
    productTourState: { tourActive }
  } = useProductTourContext()

  useMount(() => {
    trackEvent(trackEvents.CREATE_CAREER_STEP_3_VIEWED)

    if (tourActive) {
      setTimeout(() => {
        setProductTourState({ run: true })
      }, 0.4 * 1000)
    }
  })

  const {
    data: careerAnalyses,
    status: careerAnalysesStatus,
    refetch: refetchCareerAnalyses
  } = useQuery({
    queryKey: QUERY_KEYS.analysesByCareer(career.id),
    queryFn: () =>
      query({
        query: 'careerAnalyseByCareer',
        variables: { career_id: career.id }
      }),
    select: (data) =>
      data.map((analyse) => ({
        ...analyse,
        preset: analyse.preset ? JSON.parse(analyse.preset) : null
      }))
  })

  const { data: careersV2, status: careersStatus } = useQuery({
    queryKey: QUERY_KEYS.careersWithAnalysesByPartner(partner.id),
    queryFn: async () => {
      try {
        const careers_response = await query({
          query: 'careerByPartnerByDate',
          variables: { partner_id: partner.id }
        })

        const active_careers_v2 = careers_response.filter(
          ({ status, archived, diagnostic_version }) =>
            status === 'ACTIVE' && !archived && diagnostic_version === 2
        )

        for (const career of active_careers_v2) {
          try {
            const analyses = await query({
              query: 'careerAnalyseByCareer',
              variables: { career_id: career.id }
            })

            career.analyses = analyses
          } catch (err) {
            // Handle authorization errors for copied careers from other customers
            console.warn(
              `Failed to fetch analyses for career ${career.id}:`,
              err
            )
            career.analyses = []
          }
        }

        const careers_with_analyses = active_careers_v2.filter(
          ({ analyses }) => !!analyses.length
        )

        return careers_with_analyses
      } catch (err) {
        console.error('Failed to fetch careers with analyses:', err)
        return []
      }
    }
  })

  const {
    isLoadingBack,
    isLoadingNext,
    handleBackNavigation,
    handleNextNavigation
  } = useCreateCareerNavigation({ career, careerMutation })

  const generateRequirementProfile = async () => {
    const pdfDocument = window.open('', '_blank')
    pdfDocument.document.writeln(t('pdf_preparation'))

    try {
      await query({
        query: 'synthesizeAnalyze',
        variables: { career_id: career.id }
      })

      const pdfResponse = await query({
        query: 'generatePdf',
        variables: {
          career_id: career.id,
          language,
          type: REQUIREMENTPROFILE
        }
      })

      pdfDocument.location.href = JSON.parse(pdfResponse).headers.Location
      success(t('pdf_success'))
      trackEvent(trackEvents.PREVIEW_REQUIREMENT_PROFILE, {
        diagnostic_version: 2 // /create-career at the moment always 2
      })
    } catch (err) {
      error()
      notifyBugsnag(err)
      pdfDocument.close()
    }
  }

  const showNotAllAnalysesCompletedHint = () => {
    alert(
      t(TRANSLATION_LOCATION + 'modal_not_all_analyses_completed_title'),
      t(TRANSLATION_LOCATION + 'modal_not_all_analyses_completed_text'),
      [
        {
          onPress: () => {},
          text: t(
            TRANSLATION_LOCATION + 'modal_not_all_analyses_completed_action'
          )
        }
      ]
    )
  }

  if (careerAnalysesStatus === 'pending' || careersStatus === 'pending') {
    return <ActivityIndicator />
  }

  const careerAnalysesVerifiedCount = careerAnalyses.filter(
    ({ status }) => status === 'COMPLETED'
  ).length

  return (
    <>
      <div className='mx-auto max-w-5xl'>
        <div className='mb-8'>
          <h1 className='text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl'>
            {t('create_career.career_analyse.title')}
          </h1>
          <div className='w-full sm:w-3/4'>
            <p className='mt-3 text-base text-gray-600'>
              {t('create_career.career_analyse.subtitle')}
            </p>
            <ul className='mt-4 space-y-2'>
              <li className='flex flex-row items-center gap-2 text-sm text-gray-700'>
                <CheckCircleIcon className='size-5 flex-shrink-0 text-primaryBlue' />
                {t('create_career.career_analyse.option_1')}
              </li>
              <li className='flex flex-row items-center gap-2 text-sm text-gray-700'>
                <CheckCircleIcon className='size-5 flex-shrink-0 text-primaryBlue' />
                {t('create_career.career_analyse.option_2')}
              </li>
              <li className='flex flex-row items-center gap-2 text-sm text-gray-700'>
                <CheckCircleIcon className='size-5 flex-shrink-0 text-primaryBlue' />
                {t('create_career.career_analyse.option_3')}
              </li>
            </ul>
          </div>
        </div>
        {careerAnalyses.length === 0 && (
          <div className='mb-6 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 p-5'>
            <div className='flex items-start gap-3'>
              <div className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-blue-600'>
                <svg
                  className='h-5 w-5 text-white'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z'
                  />
                </svg>
              </div>
              <div className='flex-1'>
                <h3 className='font-semibold text-gray-900'>
                  {t('create_career.career_analyse.collaboration_hint_title')}
                </h3>
                <p className='mt-1 text-sm text-gray-600'>
                  {t(
                    'create_career.career_analyse.collaboration_hint_description'
                  )}
                </p>
                <div className='mt-3 space-y-2 text-sm text-gray-700'>
                  <p>
                    ✓{' '}
                    <strong>
                      {t(
                        'create_career.career_analyse.collaboration_hint_benefit_1'
                      )}
                    </strong>
                  </p>
                  <p>
                    ✓{' '}
                    <strong>
                      {t(
                        'create_career.career_analyse.collaboration_hint_benefit_2'
                      )}
                    </strong>
                  </p>
                  <p>
                    ✓{' '}
                    <strong>
                      {t(
                        'create_career.career_analyse.collaboration_hint_benefit_3'
                      )}
                    </strong>
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        <Analyses
          {...{
            career,
            careersV2,
            careerAnalyses,
            refetchCareerAnalyses,
            setOpenModal
          }}
        />
        {careerAnalysesVerifiedCount > 0 && (
          <div className='mt-6 flex justify-end'>
            <Button.SecondaryXL
              text={t(TRANSLATION_LOCATION + 'requirement_profile_action')}
              onClick={generateRequirementProfile}
            />
          </div>
        )}
        <CreateCareerFooter>
          <div className='flex justify-end gap-x-2'>
            <Button.SecondaryXL
              onClick={() => handleBackNavigation({})}
              isLoading={isLoadingBack}
              text={t('create_career.back_action')}
            />
            <Button.PrimaryXL
              text={t('create_career.next_action')}
              className='bg-green-600 hover:bg-green-500'
              onClick={async () => {
                try {
                  if (careerAnalyses.length !== careerAnalysesVerifiedCount) {
                    showNotAllAnalysesCompletedHint()
                    return
                  }
                  setIsLoadingSynthesizeAnalyze(true)
                  await query({
                    query: 'synthesizeAnalyze',
                    variables: { career_id: career.id }
                  })
                  setIsLoadingSynthesizeAnalyze(false)

                  handleNextNavigation({
                    // career_analyse is calculated by summarize || select preset
                    onSuccess: () => {
                      trackEvent(trackEvents.FINISH_CAREER_ANALYSE_BY_EXPERT)
                    }
                  })
                } catch (err) {
                  setIsLoadingSynthesizeAnalyze(false)
                  notifyBugsnag(err)
                }
              }}
              isLoading={isLoadingSynthesizeAnalyze || isLoadingNext}
              disabled={careerAnalysesVerifiedCount < 1}
            />
          </div>
        </CreateCareerFooter>
      </div>
      <ModalInviteExperts
        key={uuid}
        {...{
          career,
          careerAnalyses,
          refetchCareerAnalyses,
          open: openModal === 'invite-experts',
          closeModal: () => {
            setOpenModal(null)
            // reset key of invite experts modal to render it again
            setTimeout(() => setUuid(uuidSmall()), 0.6 * 1000)
          }
        }}
      />
      <ModalSelectPreset
        {...{
          career,
          refetchCareerAnalyses,
          open: openModal === 'select-preset',
          closeModal: () => setOpenModal(null)
        }}
      />
      <ModalSelectCareer
        {...{
          career,
          careersV2,
          refetchCareerAnalyses,
          open: openModal === 'select-career',
          closeModal: () => setOpenModal(null)
        }}
      />
    </>
  )
}

CareerAnalyse.propTypes = {
  career: PropTypes.object.isRequired,
  careerMutation: PropTypes.object.isRequired
}

export default CareerAnalyse
